<template>
  <div class="card h-100">
    <div class="card-header">
      <div class="header-icon">
        <i class="fa-solid fa-comments-dollar"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Financial Assistant</h3>
        <p class="card-subtitle">Ask questions about your finances</p>
      </div>
    </div>

    <div class="card-body d-flex flex-column p-0 chat-body">
      <!-- Chat messages area -->
      <div class="chat-messages" ref="messagesContainer">
        <div v-if="messages.length === 0" class="empty-state">
          <i class="fa-solid fa-wand-magic-sparkles"></i>
          <p>Ask me anything about your spending, budget, or savings goals!</p>
          <div class="example-questions">
            <button 
              v-for="example in exampleQuestions" 
              :key="example"
              @click="sendMessage(example)"
              class="example-btn"
            >
              <i class="fa-solid fa-sparkles me-2"></i>{{ example }}
            </button>
          </div>
        </div>

        <div v-for="(message, index) in messages" :key="index" class="message" :class="message.role">
          <div class="message-header">
            <span class="message-name">{{ message.role === 'user' ? 'You' : 'Finly' }}</span>
          </div>
          <div class="message-content">
            <div 
              v-if="message.role === 'assistant'" 
              class="message-text markdown-content" 
              v-html="renderMarkdown(message.content)"
            ></div>
            <div v-else class="message-text">{{ message.content }}</div>
          </div>
        </div>

        <div v-if="isLoading" class="message assistant">
          <div class="message-header">
            <span class="message-name">Finly</span>
          </div>
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input area -->
      <div class="chat-input-container">
        <div class="input-group input-group-lg">
          <input
            ref="inputField"
            v-model="userInput"
            @keypress.enter.prevent="handleSend"
            type="text"
            class="form-control"
            :placeholder="isLoading ? 'Waiting for response...' : 'Ask about your finances...'"
            :readonly="isLoading"
          />
          <button 
            @click="handleSend" 
            class="btn btn-primary"
            :disabled="isLoading || !userInput.trim()"
          >
            <i class="fa-solid fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, onMounted } from 'vue';
import { sendChatMessage } from '../utils/financialAssistantService';
import { useAuth } from '../composables/useAuth';
import MarkdownIt from 'markdown-it';

export default {
  name: 'FinancialAssistantChat',
  setup() {
    const md = new MarkdownIt({
      html: false,
      linkify: true,
      typographer: true,
      breaks: true
    });
    const { getAccessToken } = useAuth();
    const messages = ref([]);
    const userInput = ref('');
    const isLoading = ref(false);
    const messagesContainer = ref(null);
    const inputField = ref(null);
    const conversationId = ref(undefined);

    const exampleQuestions = [
      'How much did I spend on groceries this month?',
      'What\'s my biggest expense category?',
      'What was my largest purchase last month?',
      'Help me create a savings goal'
    ];

    const scrollToBottom = async () => {
      await nextTick();
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
      }
    };

    const renderMarkdown = (text) => {
      return md.render(text);
    };

    const sendMessage = async (messageText) => {
      const text = messageText || userInput.value.trim();
      if (!text || isLoading.value) return;

      // Add user message
      messages.value.push({
        role: 'user',
        content: text
      });

      userInput.value = '';
      isLoading.value = true;
      
      await scrollToBottom();

      try {
        const accessToken = await getAccessToken();

        const result = await sendChatMessage(text, conversationId.value, accessToken);
        
        // Store conversation ID for subsequent messages
        conversationId.value = result.data.conversationId;
        
        // Add assistant response
        messages.value.push({
          role: 'assistant',
          content: result.data.answer
        });

        await scrollToBottom();
      } catch (error) {
        console.error('Chat error:', error);
        messages.value.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.'
        });
      } finally {
        isLoading.value = false;
        // Refocus input after response
        await nextTick();
        inputField.value?.focus();
      }
    };

    const handleSend = () => {
      sendMessage();
    };

    return {
      messages,
      userInput,
      isLoading,
      messagesContainer,
      inputField,
      exampleQuestions,
      sendMessage,
      handleSend,
      renderMarkdown
    };
  }
};
</script>

<style scoped>
.chat-body {
  overflow: hidden;
  min-height: 450px;
}

.chat-messages {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 1rem;
  min-height: 0;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.chat-messages::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--light-text-muted);
  text-align: center;
  padding: 2rem;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.example-questions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
  max-width: 400px;
}

.example-btn {
  background: color-mix(in srgb, var(--light-primary) 8%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid color-mix(in srgb, var(--light-primary) 20%, transparent);
  border-radius: 12px;
  padding: 0.65rem 1rem;
  color: var(--light-text);
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.25s ease;
}

.example-btn:hover {
  background: color-mix(in srgb, var(--light-primary) 15%, transparent);
  border-color: color-mix(in srgb, var(--light-primary) 40%, transparent);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--light-primary) 15%, transparent);
}

.example-btn:active {
  transform: translateY(0);
}

.example-btn i {
  color: var(--light-primary);
  font-size: 0.75rem;
}

.message {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.message.user {
  align-items: flex-end;
}

.message-header {
  display: flex;
  align-items: center;
  padding: 0 0.25rem;
}

.message-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--light-text-muted);
}

.message.user .message-name {
  color: var(--light-primary);
}

.message-content {
  max-width: 70%;
}

.message-text {
  background: var(--nested-container-bg);
  padding: 0.75rem 1rem;
  border-radius: 12px;
  color: var(--light-text);
  line-height: 1.5;
  border: 2px dashed var(--light-border);
}

.message.user .message-text {
  background: var(--light-primary);
  color: white;
  border-color: var(--light-primary);
}

.markdown-content {
  word-wrap: break-word;
}

.markdown-content :deep(p:last-child) {
  margin: 0;
}

.markdown-content :deep(table) {
  margin-bottom: 0.5rem;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.markdown-content :deep(li) {
  margin-bottom: 0.25rem;
}

.markdown-content :deep(code) {
  background: rgba(0, 0, 0, 0.1);
  padding: 0.125rem 0.25rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.markdown-content :deep(pre) {
  background: rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
  border-radius: 6px;
  overflow-x: auto;
  margin: 0.5rem 0;
}

.markdown-content :deep(pre code) {
  background: none;
  padding: 0;
}

.markdown-content :deep(strong) {
  font-weight: 600;
}

.markdown-content :deep(em) {
  font-style: italic;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.markdown-content :deep(h1) { font-size: 1.5rem; }
.markdown-content :deep(h2) { font-size: 1.3rem; }
.markdown-content :deep(h3) { font-size: 1.1rem; }

.markdown-content :deep(blockquote) {
  border-left: 3px solid var(--light-border);
  padding-left: 0.75rem;
  margin: 0.5rem 0;
  color: var(--light-text-muted);
}

.markdown-content :deep(a) {
  color: #36A2EB;
  text-decoration: underline;
}

.markdown-content :deep(a:hover) {
  color: #2a8bd1;
}

.typing-indicator {
  display: flex;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  background: var(--nested-container-bg);
  border-radius: 12px;
  width: fit-content;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--light-text-muted);
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

.chat-input-container {
  padding: 1rem;
  border-top: 2px solid var(--light-border);
  background: var(--light-card);
  border-radius: 0 0 5px 5px;
}

.chat-input-container .form-control {
  border-radius: 0.5rem 0 0 0.5rem;
  border: 2px solid var(--light-border);
  background: var(--light-card);
  color: var(--light-text);
}

.chat-input-container .form-control:focus {
  border-color: #36A2EB;
  box-shadow: none;
}

.chat-input-container .btn-primary {
  border-radius: 0 0.5rem 0.5rem 0;
  min-width: 60px;
}

@media (max-width: 768px) {
  .chat-body {
    min-height: 350px;
  }

  .message-content {
    max-width: 80%;
  }

  .example-questions {
    width: 100%;
  }
}
</style>
